import {useEffect, useRef, useState} from 'react'
import {parseAppZoneDate} from '@/utils/dateUtils'

/**
 * THE WAITING CLOCK — before it starts, while it is free, and once it is charged.
 *
 * ⚠️ THE PASSENGER'S COPY of the driver portal's `WaitingTimer`, kept identical so both
 * sides read the same number — the passenger polls `GET /passengers/trips/{trip}/waiting-status`
 * (see `useWaitingStatus`). A fix to one belongs in the other.
 *
 * ── Where the number comes from, and where it does NOT ────────────────────────────
 * The SERVER owns every input. `GET /trips/{trip}/waiting-status` returns, in `live`:
 *
 *   clock_started_at        when the clock starts (APP_TIMEZONE wall clock, no zone)
 *   starts_in_seconds       how far off that is, at the moment of the response
 *   elapsed_seconds         how much of it has run, at the moment of the response
 *   free_seconds_remaining  how much of the allowance is left, at the moment of the response
 *
 * The allowance is a commercial rule that differs between pickups and stop-overs and is
 * what the passenger is billed against, so it is never a constant here: free time counts
 * down from `free_seconds_remaining`, and charged time counts up from the moment it ran out.
 *
 * ── ⚠️ THE CLOCK DOES NOT START WHEN THE DRIVER ARRIVES ───────────────────────────
 * This is the bug this version fixes. The backend starts the free-wait clock at
 * `clock_started_at` — the scheduled pickup — so a driver who arrives early gets a clock
 * that has NOT STARTED, with `starts_in_seconds` counting down to it and `elapsed_seconds`
 * and `free_seconds_remaining` sitting (correctly) at 0 and the full allowance.
 *
 * The old component ignored both of those and counted the allowance down from the moment
 * it rendered. So it restarted on every reload — the server still said 300, because 300 was
 * still true — and it hit 00:00 twenty minutes before the server had counted a second,
 * which is why paid time never appeared: there was none yet.
 *
 * ── ⚠️ ANCHORED ON A TIMESTAMP, NOT SEEDED FROM A COUNT ───────────────────────────
 * Everything shown is `now` measured against ONE fixed instant — the clock's start. A
 * reload lands in exactly the same place, a background tab that missed a hundred ticks
 * renders the right number on the next one, and nothing depends on how often we poll.
 *
 * The anchor comes from the SERVER's own figures first — `elapsed_seconds` (running) or
 * `starts_in_seconds` (not yet) at the moment it answered, placed against the moment that
 * answer arrived — so no timezone and no wrong device clock can move it. Re-seeded on every
 * poll. Only when neither is running does `clock_started_at` stand in, shifted by the
 * server/device clock offset (`server_time`) when the response carries one.
 *
 * ── ⚠️ THE CLOCK DOES NOT STOP AT ZERO, IT CHANGES MEANING ───────────────────────
 * Free time running out is the point at which the wait starts being CHARGED. A timer that
 * froze at 00:00 would hide the one transition on this screen with money attached, so past
 * the allowance it counts the billable time UP, and says so.
 *
 * @param {object}  live  the `live` block from the waiting-status resource
 * @param {boolean} bare  the digits alone, for when the place supplies the meaning
 */

/** `mm:ss`, or `h:mm:ss` past an hour — never negative. (Minutes alone once read
    "2848:25" for a start two days off.) */
function clock(totalSeconds) {
    const safe = Math.max(0, Math.floor(totalSeconds))
    const hours = Math.floor(safe / 3600)
    const minutes = Math.floor((safe % 3600) / 60)
    const seconds = safe % 60
    const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    return hours > 0 ? `${hours}:${mmss}` : mmss
}

const num = (v) => (v === null || v === undefined || v === '' ? null : (Number.isFinite(Number(v)) ? Number(v) : null))

/**
 * The instant the clock starts, in epoch ms — or null when the response gives no way to
 * know it.
 *
 * ⚠️ `clock_started_at` IS APP_TIMEZONE WALL CLOCK WITH NO ZONE. A bare `new Date()` on it
 * reads it in the DRIVER's zone, which for anyone outside Toronto moves the start by hours.
 * `parseAppZoneDate` resolves it to the real instant first.
 */
function clockStartMs(live, receivedAt) {
    /* ⚠️ THE SERVER'S OWN MEASUREMENT FIRST. `elapsed_seconds` / `starts_in_seconds` are
       worked out on the server's clock at the moment it answered — anchored to when that
       answer ARRIVED here, they give the start on this device's clock with no timezone and
       no device-clock error in the way. Re-seeded on every poll, so it never drifts. A
       reload lands on exactly the same figure, because the server — not a component that
       just mounted — says how much has run. */
    const elapsed = num(live?.elapsed_seconds)
    if (elapsed != null && elapsed > 0) return receivedAt - elapsed * 1000

    const startsIn = num(live?.starts_in_seconds)
    if (startsIn != null && startsIn > 0) return receivedAt + startsIn * 1000

    /* Neither figure is running (the exact start instant, or a response without them):
       the absolute start time. `clock_started_at` is APP_TIMEZONE wall clock with no zone —
       `parseAppZoneDate` makes it a real instant — and it is moved onto this device's clock
       by the server/device offset when the response carries the server's time. */
    const parsed = parseAppZoneDate(live?.clock_started_at)
    if (parsed && Number.isFinite(parsed.getTime())) {
        const server = parseAppZoneDate(live?.server_time) || (live?.server_time ? new Date(live.server_time) : null)
        const skew = server && Number.isFinite(server.getTime()) ? server.getTime() - receivedAt : 0
        return parsed.getTime() - skew
    }

    return null
}

/**
 * `holdUntilLanded` — ACCEPTED BUT IGNORED (2026-09-18). It used to swap the clock for an
 * "After landing" pill while a from-airport flight was still in the air. The waiting time
 * now follows the scheduled pickup alone: nothing shown before it, the free time after.
 */
/** How far a new poll may move the free-time end before it counts as a real change. */
const RESYNC_MS = 3000

/**
 * `schedule` — `{startAt, freeSeconds}`: the clock WITHOUT the server's record.
 *
 * The backend opens the waiting record only once the driver marks arrival, but the clock is
 * meant to start at the PICKUP TIME whether or not they have. Until `live` exists the timer
 * runs from `startAt` (the trip's `scheduled_at`, app-zone wall clock) with `freeSeconds`
 * from the trip's waiting policy; the moment `live` arrives, the server's figures take over.
 *
 * ⚠️ WHAT IT SHOWS BEFORE ARRIVAL IS NOT WHAT IS BILLED — the backend only charges from the
 * driver's arrival. It is the clock the passenger is being held to, not an invoice.
 */
export default function WaitingTimer({live, schedule = null, bare = false, holdUntilLanded = false}) {
    /* The moment this response arrived, stamped onto `live` by `useWaitingStatus`.
       ⚠️ READ STRAIGHT OFF `live`, NOT MIRRORED INTO STATE. It used to be copied into state
       by an effect, which lands one render AFTER the new `live` — so for that render the
       new `free_seconds_remaining` was measured against the PREVIOUS poll's arrival. In the
       last seconds of free time that dipped below zero and flashed the pill red on every
       poll. */
    const mountedAtRef = useRef(Date.now())
    const receivedAt = live?.received_at_ms ?? mountedAtRef.current

    /* One tick a second. The value is recomputed from the anchor each time, so a throttled
       or late tick never accumulates error. */
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [])

    /* ⚠️ NO "AFTER LANDING" STATE ANY MORE. The clock follows the SCHEDULED PICKUP only —
       disabled until that time (see below), then free time, then charged — whether or not a
       flight is involved. `holdUntilLanded` is still accepted from callers but ignored. */
    void holdUntilLanded

    /* No server record yet → the pickup time and the policy allowance (see `schedule`). */
    const scheduledStart = !live && schedule?.startAt ? parseAppZoneDate(schedule.startAt)?.getTime() : null
    const scheduleFree = num(schedule?.freeSeconds)
    const fromSchedule = !live && Number.isFinite(scheduledStart) && scheduleFree != null

    const startMs = live ? clockStartMs(live, receivedAt) : (fromSchedule ? scheduledStart : null)
    const free = live ? num(live.free_seconds_remaining) : (fromSchedule ? scheduleFree : null)

    /**
     * WHEN THE FREE TIME ENDS, on this device's clock — ONE instant both phases read, so the
     * dark → red switch happens exactly once and never flickers back.
     *
     * ⚠️ IT ONLY EVER MOVES EARLIER, within a few seconds. Each poll says "N seconds left" as
     * of when the server answered, and the answer takes a varying time to arrive — so taken
     * raw, every poll nudged the end a fraction later or earlier and the digits jumped by a
     * second. Keeping the earliest reading makes the countdown steady. A jump of more than
     * `RESYNC_MS` is a real change (a new waypoint, a reload of the record) and is taken.
     *
     * Once free time is gone the end is fixed once, from the server's billable time, and the
     * charged count runs up from it instead of restarting each poll.
     *
     * Written during render on purpose: it is a pure function of the polls seen so far, and
     * an effect would apply it one render late — the exact lag this component used to have.
     */
    /* ⚠️ NEVER BEHIND THE LAST POLL. `now` only moves once a second, so for up to a second
       after a poll lands it is OLDER than `receivedAt`. */
    const nowMs = Math.max(now, receivedAt)
    const sinceStart = startMs != null ? (nowMs - startMs) / 1000 : null

    const freeEndsAtRef = useRef(null)
    /* Not running yet → nothing to keep. Before the start the server repeats the FULL
       allowance on every poll, and keeping the earliest of those would start the countdown
       short. */
    if (sinceStart == null || sinceStart < 0) {
        freeEndsAtRef.current = null
    } else if (fromSchedule) {
        // No polls to reconcile — the allowance simply runs from the pickup time.
        freeEndsAtRef.current = startMs + free * 1000
    } else if (!live) {
        freeEndsAtRef.current = null
    } else if (free != null && free > 0) {
        // From the later of "this answer arrived" and "the clock started": a poll that
        // landed just BEFORE the start still carries the full allowance, which runs from the
        // start, not from the poll.
        const candidate = Math.max(receivedAt, startMs) + free * 1000
        const known = freeEndsAtRef.current
        freeEndsAtRef.current = known == null || Math.abs(candidate - known) > RESYNC_MS
            ? candidate
            : Math.min(known, candidate)
    } else if (free === 0 && freeEndsAtRef.current == null) {
        const billableSeconds = num(live.billable_seconds)
            ?? (num(live.billable_minutes) != null ? num(live.billable_minutes) * 60 : null)
        freeEndsAtRef.current = receivedAt - (billableSeconds ?? 0) * 1000
    }
    const freeEndsAt = freeEndsAtRef.current

    let phase
    let value
    if (startMs == null || free == null || freeEndsAt == null || sinceStart < 0) {
        /* ⚠️ DISABLED "00:00" UNTIL THE CLOCK STARTS — before the pickup time, or before
           the waiting record exists (the driver has not marked arrival yet). Not a
           countdown TO the pickup: for a ride days off that would read thousands of
           minutes, and it is not the allowance either. */
        phase = 'idle'
        value = clock(0)
    } else {
        /* ⚠️ THE ALLOWANCE IS NOT `free + elapsed` ONCE FREE TIME HAS RUN OUT. That sum
           equals `elapsed` at `free_seconds_remaining: 0` — which grows with every poll — so
           the charged time restarted at 00:00 every 2 seconds. Both phases read the one end
           instant above instead. */
        const freeLeft = (freeEndsAt - nowMs) / 1000
        if (freeLeft > 0) {
            phase = 'free'
            value = clock(Math.ceil(freeLeft))
        } else {
            phase = 'charged'
            value = clock(-freeLeft)
        }
    }

    const label = {
        idle: 'Waiting time starts at pickup',
        free: 'Free time remaining',
        charged: 'Waiting time charged',
    }[phase]

    /* The dark pill with lime digits, as the app draws it; red once the wait is billable;
       plain white digits while the clock has not started, so a countdown TO the clock is not
       mistaken for the allowance itself. Tabular figures so the width never jitters. */
    const pill = (
        <span
            className={`shrink-0 rounded-full px-4 py-1.5 text-base font-bold tabular-nums max-md:px-3 max-md:py-1 max-md:text-sm ${
                phase === 'charged'
                    ? 'bg-(--text-red) text-white'
                    : phase === 'idle'
                        ? 'bg-(--bg-grey) text-(--text-secondary) opacity-70'
                        : 'bg-(--bg-dark) text-[#D8F548]'
            }`}
        >
            {value}
        </span>
    )

    /**
     * ⚠️ `bare` — THE DIGITS ALONE, FOR WHEN THE PLACE SUPPLIES THE MEANING.
     *
     * On the airport pickup modal's walk step this rides in the map's top-right corner,
     * where a caption would float over a street. The pill's colour carries the state the
     * label would have said. Same clock as the captioned row — only the frame differs.
     */
    if (bare) {
        return (
            <span
                className="pointer-events-none absolute top-3 right-3 z-10 shadow-[0_2px_10px_rgba(0,0,0,0.25)] rounded-full"
                title={label}
            >
                {pill}
            </span>
        )
    }

    return (
        /* ⚠️ BORDERED, NOT FILLED, AND IT CARRIES NO SHADOW OF ITS OWN — the lift belongs to
           whatever contains it; the pill carries all the colour this row needs. */
        <section className="flex items-center justify-between gap-3 rounded-xl border border-(--border-color) px-3 py-1.5">
            {/* ⚠️ THE LABEL IS THE SMALLER HALF — the digits are what the row is read for. */}
            <p className="text-sm font-semibold text-(--text-primary) max-md:text-xs">{label}</p>

            {pill}
        </section>
    )
}
